// Data layer of the הערות (notes) tab — non-hook fetchers + helpers so
// `preloadNotesTab` can warm the SHARED `notes:{contactId}` sessionCache key
// from a contacts-table row hover, without React context.
//
// The value under that key is the cross-tab contract (overview quick-panel +
// this tab + the tab badge all read it): DisplayUserNote[] — UserNote enriched
// with authorship meta — sorted newest-first. Helpers here intentionally match
// the overview tab's semantics 1:1 (same key, same shape, same tolerant time
// parsing) so whichever tab warms the cache first serves both.
import { db, firebaseApp, storage } from "firebaseConfig";
import { fetchCached } from "../../data/sessionCache";
import { USER_NOTES_COLLECTION_NAME } from "../../../../../../@types/sharedSchema";
import type {
  RealEstateContact,
  UserNote,
  UserNotesDocument
} from "../../../../../../@types/sharedSchema";
import type { DisplayUserNote } from "hooks/useListingNotes";

// ── Cache key (SHARED — see docs/crm/customer-page-implementation.md) ────────

export const notesCacheKey = (contactId: string): string => `notes:${contactId}`;

// ── Tolerant helpers (ported from useSellerNotes / useListingNotes) ──────────

// Firestore Timestamp (.toDate), Date, or ISO string — never NaN dates.
export const parseNoteTime = (value: unknown): Date => {
  const maybeTimestamp = value as { toDate?: () => Date } | null | undefined;
  if (maybeTimestamp && typeof maybeTimestamp.toDate === "function") {
    return maybeTimestamp.toDate();
  }
  const parsed = new Date(value as string | number | Date);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

export const sortNotesNewestFirst = <T extends { noteTime: Date }>(notes: T[]): T[] =>
  [...notes].sort((a, b) => (b.noteTime?.getTime() ?? 0) - (a.noteTime?.getTime() ?? 0));

/**
 * Union of the per-note `organizationIds` — the doc-level query field. MUST be
 * `undefined` (== field removed by a full set()) when nothing is shared: the
 * `array-contains` teammate query and firestore.rules both rely on the field
 * being absent rather than `[]` (legacy invariant from useSellerNotes).
 */
export function computeDocOrgIds(notes: UserNote[]): string[] | undefined {
  const set = new Set<string>();
  for (const note of notes) {
    for (const orgId of note.organizationIds ?? []) set.add(orgId);
  }
  return set.size > 0 ? Array.from(set) : undefined;
}

/** The deprecated `contact.additionalNotes` free-text — surfaced read-only so old data is never lost. */
export const legacyAdditionalNote = (
  contact: Pick<RealEstateContact, "additionalNotes"> | null | undefined
): string | null => {
  const text = contact?.additionalNotes?.trim();
  return text ? text : null;
};

// ── One-shot fetch (the preload path) ────────────────────────────────────────

const requireCurrentUserEmail = (): string => {
  const email = firebaseApp.auth().currentUser?.email;
  // Throw (not empty-array) so fetchCached stores the error and retries later,
  // instead of caching a wrong-but-plausible empty list before auth resolves.
  if (!email) throw new Error("notesData: no authenticated user yet");
  return email;
};

/**
 * The current user's own notes doc for the contact, newest-first. Own-doc
 * lookup is a (userId, contactId) query — NOT `.doc(deterministicId)` — because
 * legacy docs have auto-generated ids (invariant preserved from useSellerNotes).
 * The mounted tab upgrades this snapshot to realtime listeners that also merge
 * org-shared teammates' notes (useContactNotes).
 */
export async function fetchOwnNotesOnce(contactId: string): Promise<DisplayUserNote[]> {
  const email = requireCurrentUserEmail();
  const snapshot = await db
    .collection(USER_NOTES_COLLECTION_NAME)
    .where("userId", "==", email)
    .where("contactId", "==", contactId)
    .limit(1)
    .get();
  if (snapshot.empty) return [];
  const data = snapshot.docs[0].data() as UserNotesDocument;
  const parsed: DisplayUserNote[] = (data.notes ?? []).map((note: UserNote) => ({
    ...note,
    noteTime: parseNoteTime(note.noteTime),
    isMine: true,
    authorEmail: email
  }));
  return sortNotesNewestFirst(parsed);
}

/**
 * Fire-and-forget warm-up of the shared notes cache. Idempotent-cheap: dedups
 * through fetchCached (inflight + stale-while-revalidate), so repeated row
 * hovers cost nothing. Never throws.
 */
export function preloadNotes(contact: RealEstateContact | null | undefined): void {
  const contactId = contact?.id;
  if (!contactId) return;
  void fetchCached(notesCacheKey(contactId), () => fetchOwnNotesOnce(contactId)).catch(
    () => undefined
  );
}

// ── Voice note upload (client → Firebase Storage, MediaUpload's flow) ────────

const audioExtensionOf = (mimeType: string): string => {
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  return "webm";
};

/**
 * Uploads a recorded voice-note blob and returns its public download URL —
 * the same client-side Storage flow as MediaUpload.tsx (ref → put →
 * getDownloadURL), but keyed by the Firebase auth uid instead of the raw
 * email so no PII lands in the storage path.
 */
export async function uploadVoiceNoteBlob(
  blob: Blob,
  mimeType: string,
  contactId: string
): Promise<string> {
  const uid = firebaseApp.auth().currentUser?.uid;
  if (!uid) throw new Error("notesData: no authenticated user yet");
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${audioExtensionOf(
    mimeType
  )}`;
  const fileRef = storage.ref().child(`user-notes-audio/${uid}/${contactId}/${fileName}`);
  await fileRef.put(blob, { contentType: mimeType });
  return fileRef.getDownloadURL();
}

// ── Small display helpers ────────────────────────────────────────────────────

/** 2.7.2026 — the meta-line date format of mock s26. */
export const formatNoteDate = (date: Date | undefined): string =>
  date && !Number.isNaN(date.getTime())
    ? `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
    : "";

/** First letters of the first two words — avatar initials (Hebrew-safe). */
export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("");
};
